import { z } from 'zod';

/**
 * Migration 054 · Pre/Post hooks (cascade model)
 *
 * Three levels of hooks compose around every flow run:
 *
 *   Project ─→ Suite ─→ Flow
 *
 *   - `beforeAll` — runs ONCE per suite (or per solo flow run).
 *   - `before`    — runs BEFORE the flow body. Setup data, login, etc.
 *   - `after`     — runs AFTER the flow body. Cleanup, logout.
 *   - `afterAll`  — runs ONCE per suite, after every flow has finished.
 *
 * Flows themselves only carry `before` / `after`: a flow runs once
 * per dispatch, so `beforeAll` / `afterAll` collapse into the same
 * thing as `before` / `after` and we'd just be making the storage
 * shape weirder.
 *
 * Inheritance:
 *   - Flows inherit project + suite hooks by default. A flow can
 *     opt out by setting `inheritProjectHooks: false` /
 *     `inheritSuiteHooks: false`.
 *   - Suites inherit project hooks by default. They can opt out
 *     with `inheritProjectHooks: false`.
 *   - Projects are the root and have no inheritance toggle.
 *
 * The hooks-resolver service walks this hierarchy at run-start to
 * build the effective `setup` / `teardown` step lists; the resolver
 * (not the runner) knows about the cascade. The runner just
 * receives a flat array of steps to execute in each phase.
 */

/** A single hook step list, identified for audit / UI re-ordering. */
export const HookSpecSchema = z.object({
  /** Stable id so the UI can render a list and the API can patch a single entry without index drift. */
  id: z.string().uuid(),
  /** Human-readable name shown in the run report's collapsed sections. */
  name: z.string().min(1).max(120),
  /**
   * Steps in this hook. Schema is `unknown` here because the
   * canonical step shape lives in `flows.ts` (`UIStepSchema`) and
   * we want hooks to support every step type without circular
   * imports. Validated at the route boundary by reusing `UIStepSchema`.
   */
  steps: z.array(z.unknown()).default([]),
  /** Optional per-hook timeout (ms). Falls back to flow-level timeout. */
  timeoutMs: z.number().int().positive().optional(),
  /**
   * Setup hooks normally fail-fast — if `before` fails, the body
   * never runs. Setting `continueOnFailure: true` flips that so a
   * non-critical hook (e.g. analytics ping) can fail without
   * aborting the run. Teardown hooks default to true (try/finally).
   */
  continueOnFailure: z.boolean().default(false),
});
export type HookSpec = z.infer<typeof HookSpecSchema>;

/**
 * Bundle of all four hook kinds. Flow-level only uses `before` /
 * `after`; suite + project levels use all four.
 */
export const HooksBundleSchema = z.object({
  beforeAll: HookSpecSchema.optional(),
  before: HookSpecSchema.optional(),
  after: HookSpecSchema.optional(),
  afterAll: HookSpecSchema.optional(),
});
export type HooksBundle = z.infer<typeof HooksBundleSchema>;

/** Flow-level hooks — `before` and `after` only. */
export const FlowHooksSchema = HooksBundleSchema.pick({ before: true, after: true });
export type FlowHooks = z.infer<typeof FlowHooksSchema>;

/** Suite-level hooks — all four kinds. */
export const SuiteHooksSchema = HooksBundleSchema;
export type SuiteHooks = z.infer<typeof SuiteHooksSchema>;

/** Project-level hooks — all four kinds. */
export const ProjectHooksSchema = HooksBundleSchema;
export type ProjectHooks = z.infer<typeof ProjectHooksSchema>;

/** PATCH body for `/api/projects/:id/hooks`. */
export const UpdateProjectHooksBodySchema = z.object({
  hooks: ProjectHooksSchema,
});
export type UpdateProjectHooksBody = z.infer<typeof UpdateProjectHooksBodySchema>;

/** PATCH body for `/api/suites/:id/hooks`. */
export const UpdateSuiteHooksBodySchema = z.object({
  hooks: SuiteHooksSchema,
  /** Allow opting out of project-level hook inheritance. */
  inheritProjectHooks: z.boolean().default(true),
});
export type UpdateSuiteHooksBody = z.infer<typeof UpdateSuiteHooksBodySchema>;

/** PATCH body for the flow update endpoint when only changing hooks. */
export const UpdateFlowHooksBodySchema = z.object({
  hooks: FlowHooksSchema,
  inheritProjectHooks: z.boolean().default(true),
  inheritSuiteHooks: z.boolean().default(true),
});
export type UpdateFlowHooksBody = z.infer<typeof UpdateFlowHooksBodySchema>;

/**
 * Resolved hook bundle the runner receives. The resolver flattens
 * the cascade into two arrays: setup steps run before the body in
 * the order returned, teardown steps run after the body in REVERSE
 * dependency order (so project teardown happens last).
 *
 * `phaseTags` mirrors the shape so a UI can render the run report
 * with a per-step "Project::beforeAll" pill without re-running the
 * resolver.
 */
export const ResolvedHooksSchema = z.object({
  setup: z.array(z.unknown()).default([]),
  teardown: z.array(z.unknown()).default([]),
  phaseTags: z.object({
    setup: z.array(z.string()).default([]),
    teardown: z.array(z.string()).default([]),
  }),
});
export type ResolvedHooks = z.infer<typeof ResolvedHooksSchema>;
